import React, { useEffect, useState } from "react";
import { Typography, Box, CircularProgress, Button } from "@mui/material";
import Lottie from "lottie-react";
import { CartService } from "../../services/functions/cartService";
import { useNavigate } from "react-router-dom";

const animationPath = "/animation/order_pending_animation.json";

const OrderPendingPage = () => {
    const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
    const [isOrderRejected, setIsOrderRejected] = useState(false);
    const [isRestaurantAccepted, setIsRestaurantAccepted] = useState(false);
    const [animationData, setAnimationData] = useState<any>(null);
    const [error, setError] = useState<string>("");
    const navigate = useNavigate()

    useEffect(() => {
        fetch(animationPath)
            .then((response) => response.json())
            .then((data) => setAnimationData(data))
            .catch((error) => console.error("Error loading Lottie animation:", error));
    }, [animationPath]);

    useEffect(() => {
        const pollStatus = async () => {
            try {
                const status = await CartService.pollPurchaseStatus();
                console.log("status", status)
                if (!status) {
                    setIsPaymentCompleted(false);
                    setIsRestaurantAccepted(false);
                    // setIsOrderRejected(true);
                } else if (status === "PAYMENT_COMPLETE") {
                    setIsPaymentCompleted(true);
                    setIsRestaurantAccepted(false)
                    const cart = await CartService.fetchCart();
                    if (!!cart && !!cart.restaurantRejectedAt) {
                        setIsOrderRejected(true)
                    }
                } else if (status === "RESTAURANT_ACCEPTED_ORDER") {
                    setIsPaymentCompleted(true);
                    setIsRestaurantAccepted(true);
                    setIsOrderRejected(false);
                }
            } catch (err) {
                setError("An error occurred while checking the order status. Please try again.");
            }
        };

        const intervalId = setInterval(pollStatus, 10000); // Poll every 10 seconds

        return () => clearInterval(intervalId); // Cleanup interval on component unmount
    }, []);

    useEffect(() => {
        const redirectToOrderConfirmed = async () => {
            console.log("isRestaurantAccepted", isRestaurantAccepted, "isPaymentCompleted", isPaymentCompleted)
            if (isRestaurantAccepted) {
                navigate("/order/success")
            }
        };

        const intervalId = setInterval(redirectToOrderConfirmed, 2000); // Poll every 2 seconds

        return () => clearInterval(intervalId); // Cleanup interval on component unmount
    }, [isRestaurantAccepted]);

    const handleRetry = () => {
        window.location.reload(); // Reload page to retry polling
    };

    if (error) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    padding: 4,
                }}
                className="patlite"
            >
                <Typography variant="h6" color="error" gutterBottom>
                    {error}
                </Typography>
                <Button
                    sx={{ background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
                    onClick={handleRetry} variant="contained" color="primary">
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                padding: 8,
            }}
            className="patlite"
        >
            <Box
                sx={{
                    backgroundColor: "#fff", // White background for the card
                    boxShadow: 3,             // Subtle shadow for the card effect
                    borderRadius: 2,         // Rounded corners
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    maxWidth: 400,
                    transform: "translateY(100px) scale(0.95)", // Start from below the screen and slightly smaller
                    animation: "slideUpBounce 1.5s cubic-bezier(0.2, 1, 0.3, 1) forwards", // Apply the slide up and bounce
                    "@keyframes slideUpBounce": {
                        "0%": {
                            opacity: 0,
                            transform: "translateY(100px) scale(0.95)", // Start below and slightly smaller
                        },
                        "50%": {
                            opacity: 1,
                            transform: "translateY(-20px) scale(1.1)", // Overshoot (go too far up and scale slightly bigger)
                        },
                        "100%": {
                            opacity: 1,
                            transform: "translateY(0) scale(1)", // Settle back to the normal position and size
                        },
                    },
                }}
            >
                {isRestaurantAccepted ? (
                    <>
                        <Lottie animationData={animationData} style={{ width: 200, height: 200 }} />
                        <Typography variant="h4" color="success" gutterBottom textAlign="center">
                            Your order is ready!
                        </Typography>
                        <Typography variant="body1" align="center" sx={{ maxWidth: 400, marginBottom: 3 }} textAlign={"center"}>
                            Redirecting you.
                        </Typography>
                    </>
                ) : isOrderRejected ?
                    <>
                        <Lottie animationData={animationData} style={{ width: 200, height: 200 }} />
                        <Typography variant="h4" gutterBottom textAlign={"center"}>
                            The Restaurant is not able to complete your order at this time.
                        </Typography>
                        <Typography variant="body1" align="center" sx={{ maxWidth: 400, marginBottom: 3 }} textAlign={"center"}>
                            We are processing a refund for you now.
                        </Typography>
                    </>
                :
                (isPaymentCompleted ? (
                    <>
                        <Lottie animationData={animationData} style={{ width: 200, height: 200 }} />
                        <Typography variant="h4" gutterBottom textAlign={"center"}>
                            Waiting for the restaurant
                        </Typography>
                        <Typography variant="body1" align="center" sx={{ maxWidth: 400, marginBottom: 3 }} textAlign={"center"}>
                            Hang tight! We're checking with the restaurant.
                        </Typography>
                    </>
                ) : (
                    <>
                        <Lottie animationData={animationData} style={{ width: 200, height: 200 }} />
                        <Typography variant="h4" gutterBottom textAlign={"center"}>
                            Preparing Your Order
                        </Typography>
                        <Typography variant="body1" align="center" sx={{ maxWidth: 400, marginBottom: 3 }} textAlign={"center"}>
                            Hang tight! We're confirming your transaction and preparing your food. This may take a few minutes. You will be redirected once your order is ready.
                        </Typography>
                    </>))
                }
            </Box>
        </Box>
    );

};

export default OrderPendingPage;
